import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  ArrowRight,
  X,
  Edit2,
  Send,
  Clock,
  Info
} from 'lucide-react';

import { API } from '../lib/api';

/**
 * GuestOnboarding Component - B2 Implementation
 * 
 * Shows after successful booking code login if user doesn't have verified email.
 * Allows user to:
 * - Confirm or enter email address
 * - Request verification email
 * - Resend verification
 * - Skip (temporarily, during booking validity)
 */
export default function GuestOnboarding({ 
  token, 
  onComplete, 
  onSkip,
  suggestedEmail,
  suggestedEmailMasked,
  isRelayEmail,
  reason
}) {
  const { language } = useLanguage();
  
  // States
  const [step, setStep] = useState('email'); // email, sent, verified
  const [email, setEmail] = useState(suggestedEmail || '');
  const [isEditingEmail, setIsEditingEmail] = useState(!suggestedEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [maskedEmail, setMaskedEmail] = useState(suggestedEmailMasked || '');
  
  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);
  
  // Email validation
  const isValidEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);
  
  // Check if it's a relay email
  const isRelayEmailCheck = useCallback((email) => {
    if (!email) return false;
    const lowerEmail = email.toLowerCase();
    const relayPatterns = [
      '@guest.airbnb.com',
      '@reply.airbnb.com',
      '@guest.booking.com',
      '@property.booking.com',
      '@messages.expedia.com',
      '@messages.vrbo.com',
      '@relay.beds24.com'
    ];
    return relayPatterns.some(pattern => lowerEmail.includes(pattern));
  }, []);
  
  // Request verification
  const handleRequestVerification = async () => {
    if (!isValidEmail(email)) {
      setError(language === 'en' 
        ? 'Please enter a valid email address'
        : 'Inserisci un indirizzo email valido');
      return;
    }
    
    if (isRelayEmailCheck(email)) {
      setError(language === 'en'
        ? 'Please use a personal email address, not the booking portal email'
        : 'Utilizza un\'email personale, non l\'indirizzo del portale di prenotazione');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/guest-account/request-verification`,
        { email, confirm_email: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setStep('sent');
        setMaskedEmail(response.data.masked_email);
        setCooldownSeconds(120); // 2 minute cooldown
        toast.success(language === 'en'
          ? 'Verification email sent!'
          : 'Email di verifica inviata!');
      } else {
        setError(response.data.error);
        if (response.data.is_relay_email) {
          // Relay email detected - prompt for personal email
          setIsEditingEmail(true);
          setEmail('');
        }
        if (response.data.already_verified) {
          // Already verified - complete onboarding
          if (onComplete) onComplete();
        }
      }
    } catch (err) {
      console.error('Verification request error:', err);
      if (err.response?.status === 429) {
        setError(language === 'en'
          ? 'Too many requests. Please wait a few minutes.'
          : 'Troppe richieste. Attendi qualche minuto.');
        setCooldownSeconds(60);
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Resend verification
  const handleResend = async () => {
    if (cooldownSeconds > 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/guest-account/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCooldownSeconds(120);
        toast.success(language === 'en'
          ? 'New verification email sent!'
          : 'Nuova email di verifica inviata!');
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError(language === 'en'
          ? 'Too many requests. Please wait a few minutes.'
          : 'Troppe richieste. Attendi qualche minuto.');
        setCooldownSeconds(60);
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Change email (go back to edit)
  const handleChangeEmail = () => {
    setStep('email');
    setIsEditingEmail(true);
    setEmail('');
    setError(null);
  };
  
  // Render email input step
  const renderEmailStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#1B4332] to-[#2D5A45] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Secure Your Access' : 'Proteggi il Tuo Accesso'}
        </h2>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'Verify your email to access your booking info anytime, even after checkout.'
            : 'Verifica la tua email per accedere alle info della prenotazione anche dopo il checkout.'}
        </p>
      </div>
      
      {/* Benefits */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">
              {language === 'en' ? 'Benefits of verification:' : 'Vantaggi della verifica:'}
            </p>
            <ul className="space-y-1 text-amber-700">
              <li>• {language === 'en' ? 'Access past and future bookings' : 'Accedi a prenotazioni passate e future'}</li>
              <li>• {language === 'en' ? 'Faster check-in next time' : 'Check-in più veloce la prossima volta'}</li>
              <li>• {language === 'en' ? 'Secure password recovery' : 'Recupero password sicuro'}</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Email Input */}
      <div className="space-y-3">
        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
          {language === 'en' ? 'Email Address' : 'Indirizzo Email'}
        </Label>
        
        {suggestedEmail && !isEditingEmail ? (
          // Show suggested email with confirm/edit options
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900 font-medium">{suggestedEmailMasked || suggestedEmail}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingEmail(true)}
                className="text-[#C5A059] hover:text-[#B8934A]"
                data-testid="edit-email-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {language === 'en' ? 'Change' : 'Modifica'}
              </Button>
            </div>
            
            {isRelayEmail && (
              <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {language === 'en' 
                  ? 'This appears to be a booking portal email. Consider using a personal email.'
                  : 'Questa sembra un\'email del portale. Considera di usare un\'email personale.'}
              </p>
            )}
          </div>
        ) : (
          // Email input field
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="email"
              type="email"
              placeholder={language === 'en' ? 'your@email.com' : 'tua@email.com'}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              className="pl-10 h-12 text-base"
              data-testid="email-input"
            />
          </div>
        )}
        
        {/* Relay email warning */}
        {email && isRelayEmailCheck(email) && (
          <p className="text-sm text-amber-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {language === 'en'
              ? 'Please use a personal email, not the booking portal address.'
              : 'Usa un\'email personale, non l\'indirizzo del portale di prenotazione.'}
          </p>
        )}
        
        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="space-y-3">
        <Button
          onClick={handleRequestVerification}
          disabled={loading || (email ? !isValidEmail(email) || isRelayEmailCheck(email) : !suggestedEmail)}
          className="w-full h-12 bg-[#1B4332] hover:bg-[#2D5A45] text-white font-medium"
          data-testid="verify-email-btn"
        >
          {loading ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              {language === 'en' ? 'Send Verification Email' : 'Invia Email di Verifica'}
            </>
          )}
        </Button>
        
        {onSkip && (
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            className="w-full text-gray-500 hover:text-gray-700"
            data-testid="skip-onboarding-btn"
          >
            {language === 'en' ? 'Skip for now' : 'Salta per ora'}
          </Button>
        )}
      </div>
      
      {/* Skip notice */}
      <p className="text-xs text-gray-500 text-center">
        {language === 'en'
          ? 'You can skip this step and verify later. Your booking info will remain accessible during your stay.'
          : 'Puoi saltare questo passaggio e verificare dopo. Le info della prenotazione resteranno accessibili durante il soggiorno.'}
      </p>
    </div>
  );
  
  // Render sent step
  const renderSentStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#C5A059] to-[#B8934A] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Check Your Email' : 'Controlla la Tua Email'}
        </h2>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'We sent a verification link to:'
            : 'Abbiamo inviato un link di verifica a:'}
        </p>
        <p className="text-lg font-medium text-gray-900 mt-2">{maskedEmail}</p>
      </div>
      
      {/* Instructions */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {language === 'en' ? 'Next steps:' : 'Prossimi passi:'}
            </p>
            <ol className="space-y-1 text-blue-700 list-decimal list-inside">
              <li>{language === 'en' ? 'Open the email from La Maisonette' : 'Apri l\'email da La Maisonette'}</li>
              <li>{language === 'en' ? 'Click the verification button' : 'Clicca sul pulsante di verifica'}</li>
              <li>{language === 'en' ? 'Done! Your email is verified' : 'Fatto! La tua email è verificata'}</li>
            </ol>
          </div>
        </div>
      </div>
      
      {/* Timer / Resend */}
      <div className="text-center space-y-3">
        {cooldownSeconds > 0 ? (
          <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            {language === 'en' 
              ? `You can resend in ${cooldownSeconds} seconds`
              : `Puoi rinviare tra ${cooldownSeconds} secondi`}
          </p>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={handleResend}
            disabled={loading}
            className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
            data-testid="resend-email-btn"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {language === 'en' ? 'Resend Email' : 'Rinvia Email'}
          </Button>
        )}
        
        <Button
          type="button"
          variant="ghost"
          onClick={handleChangeEmail}
          className="text-gray-500 hover:text-gray-700 text-sm"
          data-testid="change-email-btn"
        >
          <Edit2 className="w-4 h-4 mr-1" />
          {language === 'en' ? 'Use different email' : 'Usa email diversa'}
        </Button>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      {/* Skip option */}
      {onSkip && (
        <div className="pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            className="w-full text-gray-500 hover:text-gray-700"
          >
            {language === 'en' ? 'Continue to dashboard' : 'Vai alla dashboard'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
  
  return (
    <Card className="max-w-md mx-auto shadow-xl border-0" data-testid="guest-onboarding">
      <CardContent className="p-6">
        {step === 'email' && renderEmailStep()}
        {step === 'sent' && renderSentStep()}
      </CardContent>
    </Card>
  );
}
